/**
 * A person, as a circle.
 *
 * Two layers, and the order is the whole design: the initials are drawn first
 * and are always there, and a Gravatar is painted over them if one exists and
 * loads. That is the shape this app needs rather than the usual "load, then fall
 * back on error" — a remote image is exactly the thing that does not arrive in a
 * whelping room with no signal, and a placeholder that only appears after a
 * failed request is a hole in the header for as long as the request takes to
 * give up.
 *
 * So there is no separate "default image": the app's own initials mark IS the
 * default, which is why the Gravatar URL asks for `d=404`. Gravatar's own
 * fallbacks (identicon, monsterid, retro) are generated art in somebody else's
 * house style, and they would still be a network request.
 */
import { useEffect, useState } from 'react';

import { cn } from '@/lib/cn.ts';

/**
 * Gravatar's modern address: SHA-256 of the trimmed, lower-cased address. MD5
 * still works and is what most snippets on the web show; it is also a hash this
 * app would have to ship an implementation of, where SHA-256 is in the platform.
 *
 * `crypto.subtle` exists only in a secure context. That is https and localhost —
 * every context this app runs in — but the guard is here because the failure
 * without it is an unhandled rejection rather than a missing picture. The cast
 * is what makes that guard expressible: the DOM types declare `subtle` as always
 * present, which is true of the type and not of the runtime.
 */
const gravatarHash = async (email: string): Promise<string | null> => {
  const subtle = globalThis.crypto.subtle as SubtleCrypto | undefined;
  if (subtle === undefined) return null;

  const bytes = new TextEncoder().encode(email.trim().toLowerCase());
  const digest = await subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * `null` until the digest resolves, and `null` forever for a person whose email
 * address this app does not hold — a member document carries a display name and
 * a role, not an address, so most rows in a list are initials by construction.
 */
const useGravatar = (email: string | null): string | null => {
  /**
   * The address is stored WITH the url it hashed to, and the result is derived
   * from the two rather than cleared when `email` changes. Clearing would mean a
   * setState in the effect body — a cascading render, and one frame in which the
   * previous person's picture is still on screen, which on a shared tablet is
   * the wrong person's face beside the wrong session.
   */
  const [resolved, setResolved] = useState<{ email: string; url: string } | null>(null);

  useEffect(() => {
    if (email === null || email.trim() === '') return;

    let live = true;
    void gravatarHash(email).then((hash) => {
      // 128px covers the largest place an avatar is drawn today at 2× density.
      if (live && hash !== null) {
        setResolved({ email, url: `https://gravatar.com/avatar/${hash}?s=128&d=404` });
      }
    });

    return () => {
      live = false;
    };
  }, [email]);

  return resolved !== null && resolved.email === email ? resolved.url : null;
};

/**
 * At most two, and by grapheme rather than by `charAt`: "Żaneta" is fine either
 * way, but an emoji or a surrogate pair sliced at a code unit renders as a
 * replacement character, and a display name comes from whatever the person typed
 * into their Google account.
 */
const initialsOf = (name: string): string =>
  name
    .split(/\s+/u)
    .filter((word) => word !== '')
    .slice(0, 2)
    .map((word) => [...word][0]?.toLocaleUpperCase() ?? '')
    .join('');

type AvatarProps = {
  /** What to draw when there is no picture. An email address is a fine name. */
  name: string;
  /** Hashed for Gravatar. Omit for anyone whose address this app does not hold. */
  email?: string | null;
  className?: string;
};

export const Avatar = ({ name, email = null, className }: AvatarProps) => {
  const url = useGravatar(email);
  /**
   * The URL that failed, not a boolean: a boolean would stay true after the
   * email changes — a different person on a shared tablet — and suppress the
   * next avatar for the life of the tab.
   */
  const [broken, setBroken] = useState<string | null>(null);

  return (
    <span
      // Decorative in every place it is used so far: the avatar always sits
      // inside a control or a row that names the person in text. A circle that
      // announced the name again would be a second reading of the same thing.
      aria-hidden
      className={cn(
        'relative grid size-9 shrink-0 place-items-center overflow-hidden rounded-full',
        'bg-sunken text-sm font-semibold text-ink-soft ring-1 ring-line select-none',
        className,
      )}
    >
      {initialsOf(name)}
      {url !== null && broken !== url && (
        <img
          src={url}
          alt=""
          className="absolute inset-0 size-full object-cover"
          onError={() => setBroken(url)}
        />
      )}
    </span>
  );
};
